import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { X, Play, Pause, Music2, Loader2, Check } from "lucide-react";
import api from "@/lib/api";

const MOODS = ["calm", "upbeat", "chill", "energetic", "cinematic", "happy"];

/** Musik-Picker (Phase 3) · liest den Musik-Slot (/api/music/catalog).
 *  Läuft mit Beispiel-Tracks; schaltet automatisch auf Mubert, sobald Key gesetzt. */
export default function MusicPicker({ onClose, onSelect, selectedId }) {
  const { t } = useTranslation();
  const [mood, setMood] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [provider, setProvider] = useState("local");
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/music/catalog${mood ? `?mood=${mood}` : ""}`);
      setTracks(data.tracks || []);
      setProvider(data.provider || "local");
    } catch { setTracks([]); }
    finally { setLoading(false); }
  }, [mood]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const togglePreview = (e, track) => {
    e.stopPropagation();
    if (!track.preview_url) return;
    if (playingId === track.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(track.preview_url);
    a.volume = 0.8;
    a.play().catch(() => {});
    a.onended = () => setPlayingId(null);
    audioRef.current = a;
    setPlayingId(track.id);
  };

  const pick = (track) => {
    if (audioRef.current) audioRef.current.pause();
    onSelect(track);
    onClose();
  };

  return (
    <div data-testid="music-picker" className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl shadow-xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
          <p className="text-sm font-semibold text-[#0A0A0A] flex items-center gap-2">
            <Music2 size={16} /> {t("music.title", { defaultValue: "Musik hinzufügen" })}
            {provider === "local" && <span className="text-[9px] bg-[#F0F0F0] text-[#999] px-1.5 py-0.5 rounded-full">{t("music.samples", { defaultValue: "Beispiele" })}</span>}
          </p>
          <button data-testid="music-picker-close" onClick={onClose} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-[#F5F5F5]">
            <X size={18} className="text-[#666]" />
          </button>
        </div>

        <div className="flex gap-2 px-4 py-3 overflow-x-auto scrollbar-hide border-b border-[#EEE]">
          <button onClick={() => setMood(null)} data-testid="music-mood-all"
            className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${!mood ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] text-[#666]"}`}>
            {t("music.all", { defaultValue: "Alle" })}
          </button>
          {MOODS.map((m) => (
            <button key={m} onClick={() => setMood(m)} data-testid={`music-mood-${m}`}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap capitalize ${mood === m ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] text-[#666]"}`}>
              {m}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#999]" /></div>
        ) : tracks.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-[#999]">{t("music.empty", { defaultValue: "Keine Tracks gefunden." })}</p>
        ) : (
          <div className="divide-y divide-[#EEE]">
            {tracks.map((track) => (
              <div key={track.id} data-testid={`music-track-${track.id}`} className="flex items-center gap-3 px-4 py-3">
                <button data-testid={`music-preview-${track.id}`} onClick={(e) => togglePreview(e, track)}
                  className="h-10 w-10 shrink-0 rounded-full bg-[#F5F5F5] flex items-center justify-center hover:bg-[#EEE]">
                  {playingId === track.id ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0A0A0A] truncate">{track.title}</p>
                  <p className="text-xs text-[#999] truncate capitalize">{track.mood || track.artist || "—"}</p>
                </div>
                <button data-testid={`music-select-${track.id}`} onClick={() => pick(track)}
                  className={`h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1 ${selectedId === track.id ? "bg-white border border-[#0A0A0A] text-[#0A0A0A]" : "bg-[#0A0A0A] text-white hover:bg-[#222]"}`}>
                  {selectedId === track.id ? <><Check size={13} /> {t("music.selected", { defaultValue: "Gewählt" })}</> : t("music.select", { defaultValue: "Wählen" })}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
